export class HttpError extends Error {
  status: number;
  url: string;
  body: unknown;
  headers: Headers;

  constructor(message: string, opts: { status: number; url: string; body: unknown; headers: Headers }) {
    super(message);
    this.name = "HttpError";
    this.status = opts.status;
    this.url = opts.url;
    this.body = opts.body;
    this.headers = opts.headers;
  }

  /**
   * Extracts the detail message from the error body.
   * Looks for common error message fields: detail, message, or string body.
   */
  get detailMessage(): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const body = this.body as any;
    return (
      body?.detail ||
      body?.message ||
      (typeof this.body === "string" ? this.body : "") ||
      this.message
    );
  }

  /**
   * Check if an error is an HttpError with a 404 status code.
   * Useful for ignoring "not found" errors when fetching settings
   * that may not exist yet for new tenants.
   * @param error - The caught error (can be any type)
   * @returns True if the error is an HttpError with status 404
   */
  static isNotFound(error: unknown): boolean {
    return error instanceof HttpError && error.status === 404;
  }

  /**
   * Utility to extract a user-friendly error message from any error type.
   * Handles HttpError, standard Error, and unknown error types.
   * @param error - The caught error (can be any type)
   * @param fallbackMessage - Optional fallback message if no message can be extracted
   * @returns The extracted error message or the fallback
   */
  static getErrorMessage(error: unknown, fallbackMessage = "An unexpected error occurred"): string {
    if (error instanceof HttpError) {
      return error.detailMessage || fallbackMessage;
    }
    
    if (error instanceof Error) {
      return error.message || fallbackMessage;
    }
    
    if (typeof error === "string") {
      return error || fallbackMessage;
    }
    
    return fallbackMessage;
  }
}

/**
 * Safely parse the response body, preferring JSON when appropriate.
 * Returns the parsed body or text, falling back to null if parsing fails.
 */
async function parseResponseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";

  // If server says it's JSON, parse it as JSON.
  if (contentType.includes("application/json") || contentType.includes("application/problem+json")) {
    try {
      return await response.json();
    } catch {
      // fall through to text
    }
  }

  // Otherwise try text
  try {
    const text = await response.text();
    return text;
  } catch {
    return null;
  }
}

/**
 * Creates an HttpError with a parsed response body.
 * Message is for logs; structured body is what the UI uses.
 * @param response - The failed Response object
 * @param url - The URL that was requested
 * @returns A Promise that resolves to an HttpError
 */
export async function createHttpError(response: Response, url: string): Promise<HttpError> {
  const body = await parseResponseBody(response);
  
  const message =
    typeof body === "string"
      ? body
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      : (body as any)?.detail || (body as any)?.message || `HTTP ${response.status}`;

  return new HttpError(message, {
    status: response.status,
    url,
    body,
    headers: response.headers
  });
}
